import { inject, Injectable } from '@angular/core'
import { toSignal } from '@angular/core/rxjs-interop'
import { Store } from '@ngxs/store'
import { combineLatest, merge, Observable, of } from 'rxjs'
import { catchError, distinctUntilChanged, map, shareReplay, startWith, switchMap } from 'rxjs/operators'
import { FeathersService } from '../feathers.service'
import { coerceToArray } from '../utils'
import { MyPermission } from '../../models/myPermission'
import { PermissionName } from '../../models/permissionName'
import { ActiveAllocationCurrentUser } from '../active-allocation-manager/active-allocation-manager.service'

@Injectable({
  providedIn: 'root',
})
export class MyPermissionService {
  private feathersService = inject(FeathersService)
  private store = inject(Store)

  /**
   * On fetch failure emit `[]` (fail-closed: permission checks resolve to `false`) rather than
   * erroring. The DataStore/`find` only re-throws errors, so without this an errored
   * `shareReplay(refCount:false)` would cache the failure and poison every consumer for the session.
   */
  private readonly permissions$: Observable<MyPermission[]> = this.feathersService.find(MyPermission).pipe(
    catchError((error) => {
      console.error('Failed to load permissions:', error)
      return of<MyPermission[]>([])
    }),
    shareReplay({ bufferSize: 1, refCount: false }),
  )

  /**
   * Active allocation, read via a typed lambda selector to avoid a circular import with `UserState`
   * (which injects this service). `user?` is optional because `toSignal` subscribes at service
   * construction, which can run before the NGXS `user` state slice is initialized.
   */
  private readonly activeAllocation$: Observable<ActiveAllocationCurrentUser | null> = this.store.select(
    (state: { user?: { activeAllocationCurrentUser: ActiveAllocationCurrentUser | null } }) =>
      state.user?.activeAllocationCurrentUser ?? null,
  )

  // Signal snapshots for synchronous template-friendly checks (`can`). Both read the NGXS store, so
  // nothing subscribes to `permissions$` (a `feathers.find`) at service construction — which happens
  // at app bootstrap (UserState injects this service), before authentication. Such a pre-auth request
  // returns NotAuthenticated and the Feathers client's global error hook logs the user out.
  // Permissions are put into the store post-auth by the LoadPermissions action (which calls `get()`).
  private readonly permissions = toSignal(
    this.store.select((state: { user?: { permissions?: MyPermission[] } }) => state.user?.permissions ?? []),
    { initialValue: [] as MyPermission[] },
  )
  private readonly activeAllocation = toSignal(this.activeAllocation$, {
    initialValue: null as ActiveAllocationCurrentUser | null,
  })

  // Re-resolved on every login/logout; `false` while logged out.
  private readonly superadmin$: Observable<boolean> = merge(
    this.feathersService.login$,
    this.feathersService.logout$,
  ).pipe(
    startWith(undefined),
    switchMap(() => this.feathersService.getCurrentUser() ?? of(null)),
    map((user) => !!user?.isSuperadmin()),
  )
  private readonly superadmin = toSignal(this.superadmin$, { initialValue: false })

  get(): Observable<MyPermission[]> {
    return this.permissions$
  }

  /**
   * Whether the current user holds any of the given permissions in their active allocation —
   * synchronous and reactive, for templates: `@if (permissionService.can(PermissionName.LeaveManage))`.
   * `PermissionName.Superadmin` matches the superadmin role instead of an allocation permission.
   * Reads signals, so views re-evaluate when permissions or the active allocation change.
   */
  can(permission: PermissionName | PermissionName[]): boolean {
    return coerceToArray(permission).some((p) =>
      p === PermissionName.Superadmin ? this.superadmin() : this.holds(this.permissions(), this.activeAllocation(), p),
    )
  }

  /**
   * Observable variant of `can`, for async/imperative use (e.g. route guards) where permissions may
   * not have loaded yet and a synchronous read would be unsafe.
   */
  hasPermission(permission: PermissionName | PermissionName[]): Observable<boolean> {
    return combineLatest([this.permissions$, this.activeAllocation$, this.superadmin$]).pipe(
      map(([perms, active, superadmin]) =>
        coerceToArray(permission).some((p) =>
          p === PermissionName.Superadmin ? superadmin : this.holds(perms, active, p),
        ),
      ),
      distinctUntilChanged(),
    )
  }

  private holds(
    perms: MyPermission[],
    active: ActiveAllocationCurrentUser | null,
    permission: PermissionName,
  ): boolean {
    if (!active) return false
    const entry = perms.find((p) => p.allocationId === active.allocationId)
    return entry?.permissions.some((x) => x.name === permission) ?? false
  }
}
